import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { goBackOrHome } from '../../lib/navigation';
import { ArrowLeft, History, Users, Trophy, Calendar, UserPlus } from 'lucide-react';
import { useLeagueStore } from '../../lib/leagueStore';
import { useLanguageStore } from '../../lib/languageStore';
import { useThemeStore } from '../../lib/themeStore';
import { translate } from '../../lib/translations';
import { usePageTracking } from '../../lib/hooks/usePageTracking';
import { TradeHistory } from './TradeHistory';
import FreeAgentSignings from './FreeAgentSignings';

type HistoryTab = 'trades' | 'champions' | 'events' | 'freeagents';

export function LeagueHistory() {
  // Tracker cette page
  usePageTracking('myoffice');

  const navigate = useNavigate();
  const selectedLeague = useLeagueStore((state) => state.selectedLeague);
  const { language } = useLanguageStore();
  const { theme } = useThemeStore();
  const [activeTab, setActiveTab] = useState<HistoryTab>('trades');

  if (!selectedLeague) {
    navigate('/dashboard');
    return null;
  }

  return (
    <div className="container-fluid px-3 px-md-4">
      <div className="row justify-content-center">
        <div className="col-12 col-xl-11">
          <div className="card shadow-lg border-0 glass-effect">
            <div className="card-body p-3 p-md-5">
              {/* Header */}
              <div className="d-flex align-items-center justify-content-between mb-4">
                <button
                  className={`btn btn-link p-0 d-flex align-items-center ${theme === 'dark' ? 'text-white' : 'text-primary'}`}
                  onClick={() => goBackOrHome(navigate)}
                >
                  <ArrowLeft size={20} className="me-2" />
                  {translate('back', language)}
                </button>

                <div className="text-center">
                  <h1 className={`mb-1 fw-bold fs-2 ${theme === 'dark' ? 'text-white' : 'text-primary'}`}>
                    <History size={32} className="me-2" />
                    Historique de la ligue
                  </h1>
                  <p className="text-muted mb-0">{selectedLeague.name}</p>
                </div>

                <div style={{ width: '100px' }}></div>
              </div>

              {/* Navigation Tabs */}
              <div className="card border-0 shadow-sm mb-4" style={{
                background: theme === 'dark' 
                  ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.8) 0%, rgba(15, 23, 42, 0.8) 100%)'
                  : 'linear-gradient(135deg, rgba(248, 250, 252, 0.8) 0%, rgba(241, 245, 249, 0.8) 100%)',
                backdropFilter: 'blur(10px)'
              }}>
                <div className="card-body p-3">
                  <ul className="nav nav-pills justify-content-center gap-2" role="tablist">
                    <li className="nav-item" role="presentation">
                      <button
                        className={`nav-link px-4 py-2 d-flex align-items-center gap-2 fw-semibold ${
                          activeTab === 'trades' 
                            ? 'active'
                            : theme === 'dark' ? 'text-light' : 'text-dark'
                        }`}
                        onClick={() => setActiveTab('trades')}
                        style={{
                          borderRadius: '25px',
                          transition: 'all 0.3s ease',
                          ...(activeTab === 'trades' ? {
                            background: 'linear-gradient(45deg, #3b82f6, #2563eb)',
                            color: 'white !important',
                            boxShadow: '0 4px 15px rgba(59, 130, 246, 0.3)'
                          } : {})
                        }}
                      >
                        <Users size={18} />
                        <span className="d-none d-md-inline">Historique des échanges</span>
                        <span className="d-md-none">Échanges</span>
                      </button>
                    </li>
                    
                    <li className="nav-item" role="presentation">
                      <button
                        className={`nav-link px-4 py-2 d-flex align-items-center gap-2 fw-semibold ${
                          activeTab === 'freeagents' 
                            ? 'active'
                            : theme === 'dark' ? 'text-light' : 'text-dark'
                        }`}
                        onClick={() => setActiveTab('freeagents')}
                        style={{
                          borderRadius: '25px',
                          transition: 'all 0.3s ease',
                          ...(activeTab === 'freeagents' ? {
                            background: 'linear-gradient(45deg, #3b82f6, #2563eb)',
                            color: 'white !important',
                            boxShadow: '0 4px 15px rgba(59, 130, 246, 0.3)'
                          } : {})
                        }}
                      >
                        <UserPlus size={18} />
                        <span className="d-none d-md-inline">Signatures agents libres</span>
                        <span className="d-md-none">Signatures</span>
                      </button>
                    </li>
                    
                    <li className="nav-item" role="presentation">
                      <button
                        className={`nav-link px-4 py-2 d-flex align-items-center gap-2 fw-semibold ${
                          activeTab === 'champions' 
                            ? 'active'
                            : theme === 'dark' ? 'text-light' : 'text-dark'
                        }`}
                        onClick={() => setActiveTab('champions')}
                        style={{
                          borderRadius: '25px',
                          transition: 'all 0.3s ease',
                          ...(activeTab === 'champions' ? {
                            background: 'linear-gradient(45deg, #3b82f6, #2563eb)',
                            color: 'white !important',
                            boxShadow: '0 4px 15px rgba(59, 130, 246, 0.3)'
                          } : {})
                        }}
                      >
                        <Trophy size={18} />
                        <span className="d-none d-md-inline">Champions</span>
                        <span className="d-md-none">Trophées</span>
                      </button>
                    </li>

                    <li className="nav-item" role="presentation">
                      <button
                        className={`nav-link px-4 py-2 d-flex align-items-center gap-2 fw-semibold ${
                          activeTab === 'events' 
                            ? 'active'
                            : theme === 'dark' ? 'text-light' : 'text-dark'
                        }`}
                        onClick={() => setActiveTab('events')}
                        style={{
                          borderRadius: '25px',
                          transition: 'all 0.3s ease',
                          ...(activeTab === 'events' ? {
                            background: 'linear-gradient(45deg, #3b82f6, #2563eb)',
                            color: 'white !important',
                            boxShadow: '0 4px 15px rgba(59, 130, 246, 0.3)'
                          } : {})
                        }}
                      >
                        <Calendar size={18} />
                        <span className="d-none d-md-inline">Événements marquants</span>
                        <span className="d-md-none">Événements</span>
                      </button>
                    </li>
                  </ul>
                </div>
              </div>

              {/* Tab Content */}
                <div className="tab-content">
                {activeTab === 'trades' && <TradeHistory language={language} />}
                {activeTab === 'freeagents' && <FreeAgentSignings language={language} />}
                {activeTab === 'champions' && (
                  <div className="text-center p-5">
                    <Trophy size={48} className="text-muted mb-3" />
                    <h3 className="text-muted">Champions de la ligue</h3>
                    <p className="text-muted">Cette section sera disponible prochainement...</p>
                  </div>
                )}
                {activeTab === 'events' && (
                  <div className="text-center p-5">
                    <Calendar size={48} className="text-muted mb-3" />
                    <h3 className="text-muted">Événements marquants</h3>
                    <p className="text-muted">Cette section sera disponible prochainement...</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
